import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { raiseRiskApi } from "./api/raiseRiskApi";
import { riskDetailsApi } from "./api/riskDetailsApi";
import { queryClient } from "../../shared/providers/QueryProvider";
import { useState } from "react";

// create / edit risk mutation

export const useCreateRisk = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ data }: { data: any }) => {
            return await raiseRiskApi.createRisk({ data });
        },
        onSuccess: async () => {
            await qc.refetchQueries({ queryKey: ["live-progress-risks"] });
        },
    });
};

export const useEditRisk = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ riskId, data }: { riskId: string, data: any }) => {
            return await raiseRiskApi.editRisk({ riskId, data });
        },
        onSuccess: async () => {
            await qc.refetchQueries({ queryKey: ["live-progress-risks"] });
        },
    });
};

// create / edit cause mutation

export const useCreateCause = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ riskId, data }: { riskId: string, data: any }) => {
            return await raiseRiskApi.createCause({ riskId, data });
        },
        onSuccess: async () => {
            await qc.refetchQueries({ queryKey: ["live-progress-risks"] });
        },
    });
};

export const useEditCause = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ riskId, causeId, data }: { riskId: string, causeId: string, data: any }) => {
            return await raiseRiskApi.editCause({ riskId, causeId, data });
        },
        onSuccess: async () => {
            await qc.refetchQueries({ queryKey: ["live-progress-risks"] });
        },
    });
};

// create / edit assessment mutation

export const useCreateAssessment = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ riskId, data }: { riskId: string, data: any }) => {
            return await raiseRiskApi.createAssessmement({ riskId, data });
        },
        onSuccess: async () => {
            await qc.refetchQueries({ queryKey: ["live-progress-risks"] });
            await qc.invalidateQueries({ queryKey: ["project-risk-matrix"] });
            await qc.invalidateQueries({ queryKey: ["project-risk-register"] });
        }
    });
};

export const useEditAssessment = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ riskId, assessmentId, data }: { riskId: string, assessmentId: string, data: any }) => {
            return await raiseRiskApi.editAssessmement({ riskId, assessmentId, data });
        },
        onSuccess: async () => {
            await qc.refetchQueries({ queryKey: ["live-progress-risks"] });
            await qc.invalidateQueries({ queryKey: ["project-risk-matrix"] });
            await qc.invalidateQueries({ queryKey: ["project-risk-register"] });
        }
    });
};

// create / edit mitigation mutation

export const useCreateMitigation = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ riskId, data }: { riskId: string, data: any }) => {
            return await raiseRiskApi.createMitigation({ riskId, data });
        },
        onSuccess: async () => {
            await qc.refetchQueries({ queryKey: ["live-progress-risks"] });
            await qc.invalidateQueries({ queryKey: ["project-risk-matrix"] });
            await qc.invalidateQueries({ queryKey: ["project-risk-register"] });
        }
    });
};

export const useEditMitigation = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ riskId, mitigationId, data }: { riskId: string, mitigationId: string, data: any }) => {
            return await raiseRiskApi.editMitigation({ riskId, mitigationId, data });
        },
        onSuccess: async () => {
            await qc.refetchQueries({ queryKey: ["live-progress-risks"] });
            await qc.invalidateQueries({ queryKey: ["project-risk-matrix"] });
            await qc.invalidateQueries({ queryKey: ["project-risk-register"] });
        }
    });
};

// fetching hooks

interface RiskOverviewFilters {
    projectId: string | undefined;
    assignedToMe?: boolean;
    quickFilters?: any;
    status?: string;
    category?: string;
}

export const useCriticalRisks = ({ projectId, assignedToMe, quickFilters, status, category }: RiskOverviewFilters) => {
    const { data, isFetching, isPending, isError, error } = useQuery({
        queryKey: ["project-critical-risks", projectId, assignedToMe, quickFilters?.workstream, quickFilters?.site, quickFilters?.programme, quickFilters?.region, quickFilters?.['worktype-group'], quickFilters?.projectYear, status, category],
        queryFn: () => riskDetailsApi.fetchCriticalRisks({ projectId, assignedToMe, quickFilters, status, category })
    });
    return {
        criticalRiskData: data?.data,
        isPending, isFetching, isError, error,
        removeCache: () => { queryClient.removeQueries({ queryKey: ["project-critical-risks"] }); }
    }
};

export const useRiskCategories = ({ projectId, assignedToMe, quickFilters, status, category }: RiskOverviewFilters) => {
    const { data, isFetching, isPending, isError, error } = useQuery({
        queryKey: ["project-risk-categories", projectId, assignedToMe, quickFilters?.workstream, quickFilters?.site, quickFilters?.programme, quickFilters?.region, quickFilters?.['worktype-group'], quickFilters?.projectYear, status, category],
        queryFn: () => riskDetailsApi.fetchRiskCategories({ projectId, assignedToMe, quickFilters, status, category })
    });
    return {
        riskCategoryData: data?.data || [],
        isPending, isFetching, isError, error,
        removeCache: () => { queryClient.removeQueries({ queryKey: ["project-risk-categories"] }); }
    }
};

export const useRiskMatrix = ({ projectId, assignedToMe, quickFilters, status, category }: RiskOverviewFilters) => {
    const { data, isFetching, isPending, isError, error } = useQuery({
        queryKey: ["project-risk-matrix", projectId, assignedToMe, quickFilters?.workstream, quickFilters?.site, quickFilters?.programme, quickFilters?.region, quickFilters?.['worktype-group'], quickFilters?.projectYear, status, category],
        queryFn: () => riskDetailsApi.fetchRiskMatrix({ projectId, assignedToMe, quickFilters, status, category })
    });
    return {
        riskMatrixData: data?.data,
        isPending, isFetching, isError, error,
        removeCache: () => { queryClient.removeQueries({ queryKey: ["project-risk-matrix"] }); }
    }
};

export const useRiskSummary = (projectId: string | undefined) => {
    const { data, isFetching, isPending, isError, error } = useQuery({
        queryKey: ["project-risk-summary", projectId],
        queryFn: () => riskDetailsApi.fetchRiskSummary(projectId)
    });
    return {
        riskSummaryData: data?.data,
        isPending, isFetching, isError, error,
        removeCache: () => { queryClient.removeQueries({ queryKey: ["project-risk-summary"] }); }
    }
};

export const useFetchRisk = ({ riskId }: { riskId: string | null }) => {
    const normalizedId = riskId === 'null' ? null : riskId;
    const { data, isFetching, isPending, isError, refetch } = useQuery({
        queryKey: ["project-risk-details", riskId],
        queryFn: () => riskDetailsApi.fetchRiskDetail({ riskId: normalizedId }),
        enabled: !!normalizedId
    });
    return {
        riskData: data?.data,
        isRiskPending: isPending,
        isFetching, isError,
        refetch: refetch
    }
};

export const useRiskRegister = ({ projectId, searchKey, assignedToMe, quickFilters, status, category, impactScore, probabilityScore }: { projectId: string | undefined, searchKey?: string, assignedToMe?: boolean, quickFilters?: any, status?: string, category?: string, impactScore?: string, probabilityScore?: string }) => {

    const [page, setPage] = useState<number>(1);
    const [limit, setLimit] = useState<number>(25);

    const changePage = (page: number) => {
        setPage(page);
    };

    const changeRowsPerPage = (limit: number) => {
        setLimit(limit);
        setPage(1);
    };

    const { data, isFetching, isPending, isRefetching, isError, refetch } = useQuery({
        queryKey: ["project-risk-register", projectId, searchKey, assignedToMe, quickFilters?.workstream, quickFilters?.site, quickFilters?.programme, quickFilters?.region, quickFilters?.['worktype-group'], quickFilters?.projectYear, status, category, impactScore, probabilityScore, page, limit],
        queryFn: () => {
            return riskDetailsApi.fetchRiskRegister({
                projectId,
                searchKey,
                assignedToMe,
                quickFilters,
                status,
                category,
                impactScore,
                probabilityScore,
                page,
                limit
            })
        }
    });

    return {
        isFetching,
        isError,
        refetch: refetch,
        riskRegisterData: data?.data?.rows,
        isPending: isPending,
        pagination: data?.data?.pagination,
        changePage, changeRowsPerPage,
    };

};